import { create } from 'zustand';
import { fetchApi } from '../api/client';

interface F1State {
  /** Generic data cache keyed by "type:params" */
  cache: Record<string, unknown>;
  loading: Record<string, boolean>;
  errors: Record<string, string | null>;

  /** Sidebar state */
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;

  /** Command palette state */
  paletteOpen: boolean;
  setPaletteOpen: (open: boolean) => void;

  /** Fetch data and cache by key */
  fetchData: <T = unknown>(
    type: string,
    params?: Record<string, string>,
  ) => Promise<T>;
}

function cacheKey(type: string, params?: Record<string, string>): string {
  if (!params || Object.keys(params).length === 0) return type;
  const sorted = Object.entries(params).sort(([a], [b]) => a.localeCompare(b));
  return `${type}:${sorted.map(([k, v]) => `${k}=${v}`).join('&')}`;
}

export const useStore = create<F1State>((set, get) => ({
  cache: {},
  loading: {},
  errors: {},

  sidebarOpen: false,
  setSidebarOpen: (open) => set({ sidebarOpen: open }),

  paletteOpen: false,
  setPaletteOpen: (open) => set({ paletteOpen: open }),

  fetchData: async <T = unknown>(
    type: string,
    params?: Record<string, string>,
  ): Promise<T> => {
    const key = cacheKey(type, params);
    const state = get();

    // Return cached data if available
    if (state.cache[key] !== undefined) {
      return state.cache[key] as T;
    }

    // Already loading
    if (state.loading[key]) {
      // Wait for existing fetch
      return new Promise<T>((resolve, reject) => {
        const check = () => {
          const s = get();
          if (!s.loading[key]) {
            if (s.errors[key]) reject(new Error(s.errors[key]!));
            else resolve(s.cache[key] as T);
          } else {
            setTimeout(check, 50);
          }
        };
        check();
      });
    }

    set((s) => ({
      loading: { ...s.loading, [key]: true },
      errors: { ...s.errors, [key]: null },
    }));

    try {
      const data = await fetchApi<T>(type, params);
      set((s) => ({
        cache: { ...s.cache, [key]: data },
        loading: { ...s.loading, [key]: false },
      }));
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      set((s) => ({
        loading: { ...s.loading, [key]: false },
        errors: { ...s.errors, [key]: message },
      }));
      throw err;
    }
  },
}));

/** Hook for fetching data with loading/error state */
export function useF1Data<T = unknown>(
  type: string,
  params?: Record<string, string>,
) {
  const key = cacheKey(type, params);
  const cache = useStore((s) => s.cache[key]);
  const loading = useStore((s) => s.loading[key] ?? false);
  const error = useStore((s) => s.errors[key] ?? null);
  const fetchData = useStore((s) => s.fetchData);

  return {
    data: cache as T | undefined,
    loading,
    error,
    fetch: () => fetchData<T>(type, params),
  };
}
